<template>
  <QueryLoading v-if="isLoading" />
  <QueryError
    v-else-if="isError && data == null"
    class="h-64"
  >
    Failed to retrieve transaction report
  </QueryError>
  <div v-else class="flex h-64 w-full flex-col">
    <BaseBarChart
      :data="chartData"
      class="size-full overflow-hidden"
      @hover-datapoint="onHoverDatapoint"
    />
    <div class="shrink-0 rounded-lg bg-background-fg/5 p-4">
      <template v-if="hoveredData">
        <div class="mb-2 text-sm text-background-fg/70">
          {{ hoveredData.label }}
        </div>
        <div
          class="flex w-full items-center justify-between"
        >
          <div class="flex w-1/2 items-center">
            <div class="mr-2 flex flex-col items-center">
              <div
                class="rounded-lg px-2 py-1 text-sm text-background"
                :style="{
                  backgroundColor: `rgb(${colorCard})`,
                }"
              >
                Card
              </div>
            </div>
            <span class="font-semibold">
              {{
                formatToMerchantCurrencyString(
                  hoveredData.card,
                  {
                    compact: true,
                  }
                )
              }}
            </span>
          </div>
          <div class="flex w-1/2 items-center">
            <div
              class="mr-2 rounded-lg px-2 py-1 text-sm text-background"
              :style="{
                backgroundColor: `rgb(${colorCash})`,
              }"
            >
              Cash
            </div>
            <span class="font-semibold">
              {{
                formatToMerchantCurrencyString(
                  hoveredData.cash,
                  {
                    compact: true,
                  }
                )
              }}
            </span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type TransactionReportPeriod,
  useTransactionReport,
} from "@/services/reports/retrieveTransactionReport"
import type { ChartData } from "chart.js"
import { useCssVar } from "@vueuse/core"

const { formatToMerchantCurrencyString } = useCurrency()

const props = defineProps<{
  timePeriod: TransactionReportPeriod
}>()

const { data, isError, isLoading } = useTransactionReport(
  computed(() => props.timePeriod)
)

const isDayGraph = computed(
  () =>
    props.timePeriod === "Today" ||
    props.timePeriod === "Yesterday" ||
    (typeof props.timePeriod === "object" &&
      props.timePeriod.date)
)

const colorCard = useCssVar("--color-card")
const colorCash = useCssVar("--color-cash")

const chartData = computed<
  ChartData<"bar", number[], Date>
>(() => {
  const transactions = data.value ?? []

  return {
    labels: transactions.map(({ start }) => start),
    datasets: [
      {
        label: "Cash",
        data: transactions.map(({ cash }) => cash),
        backgroundColor: `rgb(${colorCash.value})`,
      },
      {
        label: "Card",
        data: transactions.map(({ card }) => card),
        backgroundColor: `rgb(${colorCard.value})`,
      },
    ],
  }
})

const hoveredIndex = ref()
const onHoverDatapoint = (index: number | undefined) => {
  hoveredIndex.value =
    data.value &&
    index !== undefined &&
    index >= 0 &&
    index <= data.value?.length - 1
      ? index
      : undefined
}

const totalCash = computed(() =>
  data.value
    ? data.value.reduce(
        (accumulator, { cash }) => cash + accumulator,
        0
      )
    : 0
)

const totalCard = computed(() =>
  data.value
    ? data.value.reduce(
        (accumulator, { card }) => card + accumulator,
        0
      )
    : 0
)

const hoveredData = computed(() => {
  if (!data.value) return
  return hoveredIndex.value !== undefined
    ? {
        label: isDayGraph.value
          ? formatToHourRangeString(
              data.value[hoveredIndex.value]
            )
          : formatToDate(
              data.value[hoveredIndex.value].start
            ),
        cash: data.value[hoveredIndex.value].cash,
        card: data.value[hoveredIndex.value].card,
      }
    : {
        label: dataDateRangeString.value,
        cash: totalCash.value,
        card: totalCard.value,
      }
})

const dataDateRangeString = computed(() => {
  if (!data.value) return
  const start = data.value[0].start

  const end = data.value[data.value.length - 1].start

  return isDayGraph.value
    ? formatToDate(start)
    : `${formatToDate(start)} - ${formatToDate(end)}`
})

const formatToHourRangeString = ({
  start,
  end,
}: {
  start: Date
  end: Date
}) => `${formatToTime(start)} - ${formatToTime(end)}`
</script>
